import { lazy, Suspense, useEffect } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { AppShell } from './components/AppShell'
import { ErrorBoundary } from './components/ErrorBoundary'
import { LoadingScreen, ToastViewport } from './components/ui'
import { initializeDatabase } from './lib/db'
import { hasFirebase } from './lib/firebase'
import { authService } from './services/auth-service'
import { syncService } from './services/sync-service'
import { firebaseQbankSync } from './services/firebase-qbank-sync'
import { useAppStore } from './store/app-store'

const SplashScreen = lazy(() => import('./pages/SplashScreen'))
const AuthPage = lazy(() => import('./pages/AuthPage'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const LibraryPage = lazy(() => import('./pages/LibraryPage'))
const BooksPage = lazy(() => import('./pages/BooksPage'))
const ExternalLibraryPage = lazy(() => import('./pages/ExternalLibraryPage'))
const StudyPage = lazy(() => import('./pages/StudyPage'))
const VideosPage = lazy(() => import('./pages/VideosPage'))
const QuizSetupPage = lazy(() => import('./pages/QuizSetupPage'))
const QbankWorkspacePage = lazy(() => import('./pages/QbankWorkspacePage'))
const QuizSessionPage = lazy(() => import('./pages/QuizSessionPage'))
const QuizResultsPage = lazy(() => import('./pages/QuizResultsPage'))
const QuickFirePage = lazy(() => import('./pages/QuickFirePage'))
const ClinicalCasesPage = lazy(() => import('./pages/ClinicalCasesPage'))
const NotesPage = lazy(() => import('./pages/NotesPage'))
const FlashcardsPage = lazy(() => import('./pages/FlashcardsPage'))
const SearchPage = lazy(() => import('./pages/SearchPage'))
const AiTutorPage = lazy(() => import('./pages/AiTutorPage'))
const BookmarksPage = lazy(() => import('./pages/BookmarksPage'))
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'))
const CommunityPage = lazy(() => import('./pages/CommunityPage'))
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage'))
const ProfilePage = lazy(() => import('./pages/ProfilePage'))

const AdminPage = lazy(() => import('./pages/AdminPage'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))

function Protected({ children, admin = false }: { children: React.ReactNode; admin?: boolean }) {
  const { user, hydrated, setUser } = useAppStore()
  useEffect(() => {
    if (!user) {
      void authService.restore().then((u) => { if (u) setUser(u) })
    }
  }, [user, setUser])

  if (!hydrated) return <LoadingScreen />
  if (!user) return <Navigate to="/welcome" replace />
  if (admin && user && user.role !== 'admin') return <Navigate to="/" replace />
  return <>{children}</>
}

function SplashGate() {
  return <Protected><AppShell /></Protected>
}

function ApplicationRoutes() {
  return (
    <Routes>
      <Route path="/welcome" element={<SplashScreen />} />
      <Route path="/splash" element={<Navigate to="/welcome" replace />} />
      <Route path="/auth" element={hasFirebase || import.meta.env.PROD ? <AuthPage /> : <Navigate to="/" replace />} />
      <Route element={<SplashGate />}>
        <Route index element={<DashboardPage key="home-dashboard" />} />
        <Route path="qbanks" element={<DashboardPage key="qbank-directory" catalogMode />} />
        <Route path="library" element={<LibraryPage />} />
        <Route path="books" element={<BooksPage />} />
        <Route path="library/source/:packageId" element={<ExternalLibraryPage />} />
        <Route path="study/:lessonId" element={<StudyPage />} />
        <Route path="videos" element={<VideosPage />} />
        <Route path="quiz" element={<QuizSetupPage />} />
        <Route path="quiz/workspace" element={<QbankWorkspacePage />} />
        <Route path="quiz/session" element={<QuizSessionPage />} />
        <Route path="quiz/results" element={<QuizResultsPage />} />
        <Route path="quick-fire" element={<QuickFirePage />} />
        <Route path="clinical-cases" element={<ClinicalCasesPage />} />
        <Route path="notes" element={<NotesPage />} />
        <Route path="flashcards" element={<FlashcardsPage />} />
        <Route path="search" element={<SearchPage />} />
        <Route path="ai-tutor" element={<AiTutorPage />} />
        <Route path="bookmarks" element={<BookmarksPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="community" element={<CommunityPage />} />
        <Route path="subscription" element={<SubscriptionPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="settings" element={<Navigate to="/profile" replace />} />
        <Route path="cloud-store" element={<Navigate to="/qbanks" replace />} />
        <Route path="admin" element={<Protected admin><AdminPage /></Protected>} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  )
}

export default function App() {
  const { setUser, setHydrated, setOnline, addToast, theme } = useAppStore()

  /* Hydration fallback — the splash screen also hydrates, but if a user
     navigates directly to a deep link this ensures data is still loaded. */
  useEffect(() => {
    let active = true
    let startupFinished = false
    const startupTimer = window.setTimeout(() => {
      // Authentication providers can be unreachable or blocked by the browser.
      // Never prevent the public welcome and sign-in screens from rendering.
      if (active && !startupFinished) setHydrated(true)
    }, 4500)
    Promise.all([initializeDatabase(), authService.restore()]).then(([, user]) => {
      if (active) { setUser(user); if (user) firebaseQbankSync.scheduleDaily(user.id) }
    }).catch((reason) => { if (active) addToast({ title: 'Startup recovery used', message: reason instanceof Error ? reason.message : 'Local data could not be initialized.', tone: 'error' }) }).finally(() => {
      startupFinished = true
      window.clearTimeout(startupTimer)
      if (active) setHydrated(true)
    })
    const online = () => { setOnline(true); void syncService.resume().catch(() => undefined) }
    const offline = () => setOnline(false)
    window.addEventListener('online', online)
    window.addEventListener('offline', offline)
    return () => { active = false; window.clearTimeout(startupTimer); window.removeEventListener('online', online); window.removeEventListener('offline', offline) }
  }, [addToast, setHydrated, setOnline, setUser])

  useEffect(() => {
    void syncService.resume().catch(() => undefined)
    if (!('serviceWorker' in navigator) || window.iMedDesktop || !import.meta.env.PROD) return
    navigator.serviceWorker.register('./sw.js').catch(() => undefined)
  }, [])

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    document.documentElement.dataset.theme = theme
    document.body.setAttribute('data-theme', theme)
    localStorage.setItem('imed.theme', theme)
    localStorage.setItem('MM_theme', theme)
    localStorage.setItem('mingming_theme', theme)
  }, [theme])

  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingScreen />}>
        <ApplicationRoutes />
      </Suspense>
      <ToastViewport />
    </ErrorBoundary>
  )
}
